/* Organizer Website — mandatory header (Phase 6 controlled structure).
   Logo, Home / Auctions / About / Contact, language, Sign in / Register,
   phone + email. Always present on Level 1 & Level 2 pages — the organizer
   customizes branding, never removes it. 100% white-label (no AOS chrome). */
import { Mail, Menu, Phone, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { mediaUrlFromMap } from "@/lib/api";

export const WebsiteHeader = ({ organizer, website, media, sections = [] }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const base = `/${organizer.slug}`;
  const logoUrl = mediaUrlFromMap(media, organizer.theme?.logo_media_id);
  const contact = website?.contact || {};
  const email = contact.email || organizer.contact_email;
  const hasAbout = Boolean(
    (website?.modules || []).some((m) => m.type === "about" && m.enabled) ||
      Object.keys(organizer.description || {}).length
  );

  const goAnchor = (e, anchor) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === base) {
      document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`${base}#${anchor}`);
    }
  };

  const navItems = [
    { key: "home", label: t("web.home"), to: base, testid: "website-nav-home" },
    { key: "auctions", label: t("web.auctions"), anchor: "auctions", testid: "website-nav-auctions" },
    ...(hasAbout ? [{ key: "about", label: t("web.about"), to: `${base}/about`, testid: "website-nav-about" }] : []),
    { key: "contact", label: t("web.contact"), anchor: "contact", testid: "website-nav-contact" },
    ...sections.map((s) => ({ key: s.id, label: s.label, anchor: s.anchor, testid: `website-nav-${s.id}` })),
  ];

  const linkCls =
    "text-[13px] font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200 whitespace-nowrap";

  const NavLinks = ({ vertical = false }) =>
    navItems.map((item) =>
      item.anchor ? (
        <a
          key={item.key}
          href={`${base}#${item.anchor}`}
          onClick={(e) => goAnchor(e, item.anchor)}
          data-testid={item.testid}
          className={`${linkCls} ${vertical ? "py-2 text-base" : ""}`}
        >
          {item.label}
        </a>
      ) : (
        <Link key={item.key} to={item.to} data-testid={item.testid} onClick={() => setOpen(false)} className={`${linkCls} ${vertical ? "py-2 text-base" : ""}`}>
          {item.label}
        </Link>
      )
    );

  return (
    <header data-testid="website-header" className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-black/5">
      {/* utility strip — direct contact always one glance away */}
      {(contact.phone || email) && (
        <div className="hidden md:block border-b border-black/5">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 h-8 flex items-center justify-end gap-6">
            {contact.phone && (
              <a href={`tel:${contact.phone}`} data-testid="website-header-phone" className="inline-flex items-center gap-1.5 text-[11px] text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors duration-200">
                <Phone className="w-3 h-3" aria-hidden="true" />
                {contact.phone}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} data-testid="website-header-email" className="inline-flex items-center gap-1.5 text-[11px] text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors duration-200">
                <Mail className="w-3 h-3" aria-hidden="true" />
                {email}
              </a>
            )}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 lg:px-12 h-16 flex items-center gap-6">
        <Link to={base} data-testid="website-header-brand" className="flex items-center min-w-0 shrink-0 hover:opacity-75 transition-opacity duration-200">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={organizer.name}
              className="w-auto max-w-[180px] object-contain"
              style={{ height: `${Math.min(organizer.theme?.logo_height || 36, 56)}px` }}
            />
          ) : (
            <span className="text-sm font-semibold tracking-[0.12em] uppercase text-[#0A0A0A] truncate brand-heading">
              {organizer.name}
            </span>
          )}
        </Link>

        <nav className="ml-auto hidden lg:flex items-center gap-7" aria-label={organizer.name}>
          <NavLinks />
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <LanguageSwitcher />
          {user ? (
            <Link
              to="/account"
              data-testid="website-header-account"
              className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-medium text-[#0A0A0A] hover:bg-black/5 transition-colors duration-200"
            >
              {t("web.myAccount")}
            </Link>
          ) : (
            <>
              <Link
                to="/signin"
                data-testid="website-header-signin"
                className="inline-flex items-center h-9 px-4 rounded-full text-[13px] font-medium text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors duration-200"
              >
                {t("web.signIn")}
              </Link>
              <Link
                to="/signup"
                data-testid="website-header-register"
                className="inline-flex items-center h-9 px-5 rounded-full text-[13px] font-semibold text-white transition-opacity duration-200 hover:opacity-85"
                style={{ backgroundColor: "var(--brand-button, #0A0A0A)" }}
              >
                {t("web.register")}
              </Link>
            </>
          )}
        </div>

        {/* mobile */}
        <div className="lg:hidden ml-auto flex items-center gap-1">
          <LanguageSwitcher />
          <button
            type="button"
            data-testid="website-header-menu"
            aria-label={open ? t("common.close") : t("nav.openMenu")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-black/5 transition-colors duration-200"
          >
            {open ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white px-6 py-5 flex flex-col gap-1" data-testid="website-header-mobile-menu">
          <NavLinks vertical />
          <div className="flex items-center gap-3 pt-4 mt-2 border-t border-black/5">
            {user ? (
              <Link to="/account" onClick={() => setOpen(false)} className="inline-flex items-center h-10 px-5 rounded-full text-sm font-medium bg-black/5 text-[#0A0A0A]" data-testid="website-mobile-account">
                {t("web.myAccount")}
              </Link>
            ) : (
              <>
                <Link to="/signin" onClick={() => setOpen(false)} className="inline-flex items-center h-10 px-5 rounded-full text-sm font-medium bg-black/5 text-[#0A0A0A]" data-testid="website-mobile-signin">
                  {t("web.signIn")}
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center h-10 px-5 rounded-full text-sm font-semibold text-white"
                  style={{ backgroundColor: "var(--brand-button, #0A0A0A)" }}
                  data-testid="website-mobile-register"
                >
                  {t("web.register")}
                </Link>
              </>
            )}
          </div>
          {(contact.phone || email) && (
            <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-black/5">
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-2 text-sm text-[#0A0A0A]/60">
                  <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                  {contact.phone}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="inline-flex items-center gap-2 text-sm text-[#0A0A0A]/60">
                  <Mail className="w-3.5 h-3.5" aria-hidden="true" />
                  {email}
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
};
